import React, { useState } from "react";
import { Plus, Trash2, Save, X, Radio, Play, Square, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useCrud } from "./_useCrud";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, Select, EmptyState, Badge } from "./_ui";

const PLATFORMS = ["youtube", "facebook", "twitch", "vimeo", "custom"];
const EMPTY = { title: "", description: "", platform: "youtube", embed_url: "", scheduled_at: "", cover_url: "" };

export default function LivePanel() {
  const { items, create, remove, load } = useCrud({
    listEndpoint: "/admin/live-streams",
    createEndpoint: "/admin/live-streams",
    deleteEndpoint: "/admin/live-streams",
  });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const save = async () => {
    if (!form.title || !form.embed_url) return toast.error("Titre et URL/ID requis");
    const payload = { ...form };
    if (form.scheduled_at) payload.scheduled_at = new Date(form.scheduled_at).toISOString();
    else delete payload.scheduled_at;
    const ok = await create(payload, { successMsg: "Live programmé" });
    if (ok) { setAdding(false); setForm(EMPTY); }
  };

  const startLive = async (id) => {
    try { await api.put(`/admin/live-streams/${id}/start`); toast.success("Diffusion EN DIRECT"); await load(); }
    catch (e) { toast.error("Erreur"); }
  };

  const endLive = async (id) => {
    try { await api.put(`/admin/live-streams/${id}/end`); toast.success("Diffusion arrêtée"); await load(); }
    catch (e) { toast.error("Erreur"); }
  };

  if (!items) return (<><PageHeader title="Streaming en direct" /><Spinner /></>);

  return (
    <div data-testid="admin-live">
      <PageHeader
        title="Streaming en direct"
        subtitle="Programmez et diffusez vos événements en direct sur le site A.F.B. (YouTube, Facebook, Twitch...)"
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="live-create-btn">
              <Plus size={16} /> Nouveau live
            </PrimaryButton>
          )
        }
      />

      {adding && (
        <Card className="p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Titre du direct" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="live-title" />
            <Select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
              {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
            </Select>
            <Input
              placeholder={form.platform === "youtube" ? "URL ou ID YouTube (ex: dQw4w9WgXcQ)" : "URL d'embed iframe"}
              value={form.embed_url}
              onChange={(e) => setForm({ ...form, embed_url: e.target.value })}
              data-testid="live-url"
              className="md:col-span-2"
            />
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Date programmée (facultatif)</label>
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
            </div>
            <Input placeholder="URL image de couverture (facultatif)" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} />
          </div>
          <Textarea placeholder="Description du direct" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-3" />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} data-testid="live-save"><Save size={16} /> Enregistrer</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Radio} title="Aucun live programmé" hint="Créez votre premier événement en direct A.F.B." />
      ) : (
        <div className="space-y-4">
          {items.map((s) => {
            const isLive = s.status === "live";
            return (
              <Card key={s.id} className="p-5" data-testid={`live-${s.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isLive && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-[#CE1126] text-white animate-pulse">
                          <Radio size={10} /> EN DIRECT
                        </span>
                      )}
                      {!isLive && <Badge color={s.status === "scheduled" ? "blue" : "slate"}>{s.status}</Badge>}
                      <Badge color="slate">{s.platform}</Badge>
                      <h3 className="font-display text-lg text-white">{s.title}</h3>
                    </div>
                    {s.description && <p className="text-sm text-slate-400 mt-2">{s.description}</p>}
                    <div className="text-xs text-slate-500 mt-2 break-all">{s.embed_url}</div>
                    {s.scheduled_at && (
                      <div className="text-xs text-slate-400 mt-1">
                        Programmé le {new Date(s.scheduled_at).toLocaleString("fr-FR")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex gap-2">
                      {!isLive ? (
                        <button onClick={() => startLive(s.id)} className="px-3 py-1.5 rounded-md bg-[#CE1126]/20 hover:bg-[#CE1126]/30 text-[#FCA5A5] border border-[#CE1126]/40 inline-flex items-center gap-1.5 text-xs" data-testid={`live-start-${s.id}`}>
                          <Play size={12} fill="currentColor" /> Lancer
                        </button>
                      ) : (
                        <button onClick={() => endLive(s.id)} className="px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 inline-flex items-center gap-1.5 text-xs" data-testid={`live-end-${s.id}`}>
                          <Square size={11} fill="currentColor" /> Arrêter
                        </button>
                      )}
                      <button onClick={() => remove(s.id)} className="text-slate-500 hover:text-[#CE1126]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
                {isLive && (
                  <div className="mt-4 aspect-video rounded-lg overflow-hidden bg-black border border-white/10">
                    <iframe
                      src={s.embed_url} title={s.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                    />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <div className="mt-6 text-xs text-slate-500 inline-flex items-center gap-1.5">
        <Youtube size={11} /> Astuce : pour YouTube Live, copiez simplement l'ID de la vidéo (11 caractères).
      </div>
    </div>
  );
}
